<template>
  <div class="narrative-climate-wrapper">
    <div class="page-header-row">
      <h2 class="page-title">
        Narrative & Reputation Climate
      </h2>

      <div class="header-controls">
        <div class="header-select-wrapper">
          <select
            v-model="selectedGroupAnalysisKey"
            class="header-group-select"
          >
            <option value="" disabled hidden>
              Select Group Analysis
            </option>
            <option
              v-for="(item, index) in itemGroupAnalysis"
              :key="index"
              :value="item.GROUP_ANALYSIS"
            >
              {{ item.GROUP_ANALYSIS }}
            </option>
          </select>
          <v-icon class="header-chevron-icon" size="20">
            mdi-chevron-down
          </v-icon>
        </div>

        <v-btn
          class="header-apply-btn"
          depressed
          :disabled="isLoadingPublicRelationData"
          @click="loadAllPublicRelationData()"
        >
          <v-icon color="white" size="22" left>
            {{ isLoadingPublicRelationData ? 'mdi-loading mdi-spin' : 'mdi-magnify' }}
          </v-icon>
          {{ isLoadingPublicRelationData ? 'Loading...' : 'Apply' }}
        </v-btn>

        <v-btn
          class="header-setting-btn"
          depressed
          @click="settingDialog = true"
        >
          <v-icon color="white" size="22" left>
            mdi-cog-outline
          </v-icon>
          Brand Config
        </v-btn>
      </div>
    </div>

    <div class="reputation-gauge-card">
      <div class="reputation-gauge-row">
        <div class="bhs-column">
          <div class="bhs-row">
            <div class="bhs-item bhs-card bhs-card--clickable" @click="openBhsModal('sentiment')">
              <div class="bhs-icon-label">
                <div class="bhs-svg-container">
                  <img src="/weighted-sentiment.png" alt="Weighted Sentiment" class="bhs-svg" />
                </div>
                <div class="bhs-label">
                  Weighted Sentiment
                </div>
              </div>
              <div class="bhs-value">
                {{ formatScore(brandHealth?.weighted_sentiment) }}
              </div>
            </div>

            <div class="bhs-item bhs-card bhs-card--clickable" @click="openBhsModal('authority')">
              <div class="bhs-icon-label">
                <div class="bhs-svg-container">
                  <img src="/authority-score.png" alt="Authority Score" class="bhs-svg" />
                </div>
                <div class="bhs-label">
                  Authority Score
                </div>
              </div>
              <div class="bhs-value">
                {{ formatScore(brandHealth?.authority_tier) }}
              </div>
            </div>

            <div class="bhs-item bhs-card bhs-card--clickable" @click="openBhsModal('sov')">
              <div class="bhs-icon-label">
                <div class="bhs-svg-container">
                  <svg viewBox="0 0 100 100" class="bhs-svg">
                    <defs>
                      <linearGradient id="bhs-voice-grad" x1="0%" y1="100%" x2="100%" y2="50%">
                        <stop offset="0%" stop-color="#34526f" />
                        <stop offset="65%" stop-color="#7793ab" />
                        <stop offset="100%" stop-color="#cfd8dc" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 25 75 A 35.5 35.5 0 1 1 75 75"
                      fill="none"
                      stroke="url(#bhs-voice-grad)"
                      stroke-width="10"
                      stroke-linecap="round"
                    />
                    <g fill="#1e293b">
                      <path d="M 35 46 L 46 41 L 54 36 C 55.5 36 56 37 56 39 V 57 C 56 59 55.5 60 54 60 L 46 55 L 35 50 Z" />
                      <path d="M 43 54 L 45 63 C 45 64 47 64 47 63 L 46 54 Z" />
                      <path d="M 61 43 A 7 7 0 0 1 61 53" fill="none" stroke="#1e293b" stroke-width="2.5" stroke-linecap="round" />
                      <path d="M 66 38 A 13 13 0 0 1 66 58" fill="none" stroke="#1e293b" stroke-width="2.5" stroke-linecap="round" />
                    </g>
                  </svg>
                </div>
                <div class="bhs-label">
                  Share of Voice
                </div>
              </div>
              <div class="bhs-value">
                {{ formatScore(brandHealth?.weighted_sov) }}
              </div>
            </div>
          </div>
        </div>

        <div class="gauge-visual-column">
          <div class="gauge-title">
            <h3 class="section-title">
              REPUTATION VALUE GAUGE
            </h3>
            <p class="section-subtitle">
              Horizontal Donut Split
            </p>
          </div>

          <div class="gauge-render-container">
            <svg viewBox="0 0 220 125" class="gauge-svg">
              <defs>
                <path id="path-vulnerable" d="M 28 110 A 82 82 0 0 1 66.5 39.5" fill="none" />
                <path id="path-stable" d="M 72 36.5 A 82 82 0 0 1 148 36.5" fill="none" />
                <path id="path-strong" d="M 153.5 39.5 A 82 82 0 0 1 192 110" fill="none" />
              </defs>

              <path d="M 36 110 A 74 74 0 0 1 184 110" fill="none" stroke="#d5dfeb" stroke-width="3" />

              <path d="M 20.5 110 A 90 90 0 0 1 63.8 34.3" fill="none" stroke="#e5d1cd" stroke-width="21" stroke-linecap="butt" />
              <path d="M 69.3 31.1 A 90 90 0 0 1 150.7 31.1" fill="none" stroke="#dbe3eb" stroke-width="21" stroke-linecap="butt" />
              <path d="M 156.2 34.3 A 90 90 0 0 1 199.5 110" fill="none" stroke="#a2b9cc" stroke-width="21" stroke-linecap="butt" />

              <text class="arc-text text-vulnerable">
                <textPath href="#path-vulnerable" startOffset="50%" text-anchor="middle">
                  Vulnerable
                </textPath>
              </text>
              <text class="arc-text text-stable">
                <textPath href="#path-stable" startOffset="50%" text-anchor="middle">
                  Stable
                </textPath>
              </text>
              <text class="arc-text text-strong">
                <textPath href="#path-strong" startOffset="50%" text-anchor="middle">
                  Strong
                </textPath>
              </text>

              <text x="110" y="82" text-anchor="middle" class="gauge-center-text">
                {{ formatScore(brandHealth?.bhi_index) }}
              </text>

              <line
                x1="110"
                y1="110"
                :x2="needle.x2"
                :y2="needle.y2"
                stroke="#124373"
                stroke-width="5"
                stroke-linecap="round"
              />
              <circle cx="110" cy="110" r="7.5" fill="#124373" />
            </svg>
          </div>

          <div class="gauge-footer-title">
            REPUTATION GAUGE (0-100)
          </div>
        </div>
      </div>

      <div class="gauge-explanation-container">
        <p class="explanation-paragraph">
          <span class="drop-cap-wrapper">
            <span class="score-display">
              {{ formatScore(brandHealth?.bhi_index) }}
            </span>
            <span class="trend-status">
              ({{ bhiZone }} /
              <span v-if="statusArrow" class="trend-arrow inline-arrow">
                {{ statusArrow }}
              </span>
              {{ brandHealth?.insight_result?.overall_status }})
            </span>
            <span style="font-size: 15px;">
              Reputation Value
            </span>
          </span>

          {{ brandHealth?.insight_result?.executive_summary }}
          <template v-if="brandHealth">
            <strong>Rekomendasi:</strong>
            {{ brandHealth?.insight_result?.strategic_recommendation }}
          </template>
        </p>
      </div>

      <transition name="modal-fade">
        <div
          v-if="selectedBhsCard"
          class="portal-modal-overlay"
          @click.self="closeBhsModal"
        >
          <div class="portal-modal">
            <button class="portal-modal-close" @click="closeBhsModal" aria-label="Close">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div class="portal-modal-header">
              <div>
                <h4 class="portal-modal-title">
                  {{ selectedBhsCard.title }}
                </h4>
                <span class="portal-modal-subtitle">
                  Score: {{ selectedBhsCard.value }}
                </span>
              </div>
            </div>

            <div class="portal-modal-body">
              <div class="portal-modal-block">
                <p>{{ selectedBhsCard.text || '-' }}</p>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <div class="landscape-reputation-row">
      <div class="landscape-card">
        <div class="card-header">
          <div class="card-header-text">
            <h3 class="card-title">
              INDUSTRY LANDSCAPE
            </h3>
            <p class="card-subtitle">
              Horizontal Donut Split
            </p>
          </div>
        </div>

        <div class="hbar-wrapper">
          <div class="hbar">
            <div class="hbar-seg hbar-seg--positive" :style="{ width: industryPositive + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-green"></span>{{ industryPositive }}%
              </span>
            </div>
            <div class="hbar-seg hbar-seg--neutral" :style="{ width: industryNeutral + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-blue"></span>{{ industryNeutral }}%
              </span>
            </div>
            <div class="hbar-seg hbar-seg--negative" :style="{ width: industryNegative + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-red"></span>{{ industryNegative }}%
              </span>
            </div>
          </div>
        </div>

        <div class="card-bottom">
          <div class="card-bottom-left">
            <p class="key-drivers-label">
              KEY DRIVERS
            </p>
            <p class="key-drivers-text">
              #IndustryCrises, New Regulations,<br />Competitor Strife
            </p>
          </div>
          <div class="card-bottom-right">
            <p class="weather-label">
              INDUSTRY WEATHER
            </p>
            <div class="weather-icon">
              <svg
                v-if="industryPositive > industryNegative"
                viewBox="0 0 60 50"
                width="54"
                height="44"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="22" cy="22" r="10" fill="#FDD835" />
                <line x1="22" y1="6" x2="22" y2="2" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="22" y1="38" x2="22" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="6" y1="22" x2="2" y2="22" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="38" y1="22" x2="42" y2="22" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="10" y1="10" x2="7" y2="7" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="34" y1="34" x2="37" y2="37" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="34" y1="10" x2="37" y2="7" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="10" y1="34" x2="7" y2="37" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <ellipse cx="40" cy="32" rx="14" ry="9" fill="#cfd8dc" />
                <ellipse cx="30" cy="35" rx="9" ry="7" fill="#cfd8dc" />
                <ellipse cx="50" cy="35" rx="8" ry="6" fill="#cfd8dc" />
                <rect x="21" y="33" width="38" height="9" rx="4.5" fill="#cfd8dc" />
              </svg>
              <svg
                v-else
                viewBox="0 0 60 44"
                width="54"
                height="40"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse cx="30" cy="18" rx="18" ry="11" fill="#b0bec5" />
                <ellipse cx="18" cy="22" rx="10" ry="8" fill="#b0bec5" />
                <ellipse cx="42" cy="22" rx="10" ry="8" fill="#b0bec5" />
                <rect x="8" y="20" width="44" height="10" rx="5" fill="#b0bec5" />
                <line x1="18" y1="33" x2="15" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
                <line x1="28" y1="33" x2="25" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
                <line x1="38" y1="33" x2="35" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div class="landscape-card">
        <div class="card-header">
          <div class="card-header-text">
            <h3 class="card-title">
              BRAND REPUTATION
            </h3>
            <p class="card-subtitle">
              Horizontal Donut Split
            </p>
          </div>
        </div>

        <div class="hbar-wrapper">
          <div class="hbar">
            <div class="hbar-seg hbar-seg--positive" :style="{ width: brandPositive + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-green"></span>{{ brandPositive }}%
              </span>
            </div>
            <div class="hbar-seg hbar-seg--neutral" :style="{ width: brandNeutral + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-blue"></span>{{ brandNeutral }}%
              </span>
            </div>
            <div class="hbar-seg hbar-seg--negative" :style="{ width: brandNegative + '%' }">
              <span class="hbar-label hbar-label--with-dot">
                <span class="dot dot-red"></span>{{ brandNegative }}%
              </span>
            </div>
          </div>
        </div>

        <div class="card-bottom">
          <div class="card-bottom-left">
            <p class="key-drivers-label">
              KEY DRIVERS
            </p>
            <p class="key-drivers-text">
              CEO Town Hall, CSR Campaign Echo,<br />Customer Service Praise
            </p>
          </div>
          <div class="card-bottom-right">
            <p class="weather-label">
              BRAND WEATHER
            </p>
            <div class="weather-icon">
              <svg
                v-if="brandPositive > brandNegative"
                viewBox="0 0 60 50"
                width="54"
                height="44"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="22" cy="22" r="10" fill="#FDD835" />
                <line x1="22" y1="6" x2="22" y2="2" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="22" y1="38" x2="22" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="6" y1="22" x2="2" y2="22" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="38" y1="22" x2="42" y2="22" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="10" y1="10" x2="7" y2="7" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="34" y1="34" x2="37" y2="37" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="34" y1="10" x2="37" y2="7" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="10" y1="34" x2="7" y2="37" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round"/>
                <ellipse cx="40" cy="32" rx="14" ry="9" fill="#cfd8dc" />
                <ellipse cx="30" cy="35" rx="9" ry="7" fill="#cfd8dc" />
                <ellipse cx="50" cy="35" rx="8" ry="6" fill="#cfd8dc" />
                <rect x="21" y="33" width="38" height="9" rx="4.5" fill="#cfd8dc" />
              </svg>
              <svg
                v-else
                viewBox="0 0 60 44"
                width="54"
                height="40"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse cx="30" cy="18" rx="18" ry="11" fill="#b0bec5" />
                <ellipse cx="18" cy="22" rx="10" ry="8" fill="#b0bec5" />
                <ellipse cx="42" cy="22" rx="10" ry="8" fill="#b0bec5" />
                <rect x="8" y="20" width="44" height="10" rx="5" fill="#b0bec5" />
                <line x1="18" y1="33" x2="15" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
                <line x1="28" y1="33" x2="25" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
                <line x1="38" y1="33" x2="35" y2="42" stroke="#78909c" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="media-portal-section">
      <h3 class="media-portal-title">
        MEDIA ONLINE PORTAL SUMMARIZATION: INDUSTRY KEYWORDS
      </h3>
      <div class="carousel-outer">
        <button
          class="carousel-arrow carousel-arrow--prev"
          :disabled="currentPage === 0"
          @click="prevSlide"
          aria-label="Previous"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <div class="carousel-track-wrapper">
          <div class="carousel-track" :style="trackStyle">
            <div
              class="portal-card"
              v-for="(card, idx) in prNewsCards"
              :key="idx"
              @click="openInsightModal(card)"
            >
              <div class="portal-card-top">
                <img
                  class="portal-logo-img"
                  :src="newsIconMap[card.domain]"
                  :alt="card.domain"
                />
                <div class="portal-donut">
                  <svg viewBox="0 0 48 48" width="52" height="52" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="18" fill="none" stroke="#dde4ec" stroke-width="8"/>
                    <circle
                      cx="24"
                      cy="24"
                      r="18"
                      fill="none"
                      stroke="#00E396"
                      stroke-width="8"
                      :stroke-dasharray="card.arcs.positive.dasharray"
                      :stroke-dashoffset="card.arcs.positive.dashoffset"
                      transform="rotate(-90 24 24)"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="18"
                      fill="none"
                      stroke="#008FFB" stroke-width="8"
                      :stroke-dasharray="card.arcs.neutral.dasharray"
                      :stroke-dashoffset="card.arcs.neutral.dashoffset"
                      transform="rotate(-90 24 24)"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="18"
                      fill="none"
                      stroke="#FF4560"
                      stroke-width="8"
                      :stroke-dasharray="card.arcs.negative.dasharray"
                      :stroke-dashoffset="card.arcs.negative.dashoffset"
                      transform="rotate(-90 24 24)"
                    />
                    <circle cx="24" cy="24" r="12" fill="#ffffff"/>
                  </svg>
                </div>
              </div>
              <div class="portal-card-body">
                <p class="portal-card-text">
                  <strong>{{ card.domain }}:</strong> {{ card.insightText }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <button
          class="carousel-arrow carousel-arrow--next"
          :disabled="currentPage === totalPages - 1"
          @click="nextSlide"
          aria-label="Next"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      <div class="carousel-dots">
        <span
          v-for="n in totalPages"
          :key="n"
          class="carousel-dot"
          :class="{ 'carousel-dot--active': currentPage === (n - 1) }"
          @click="goToPage(n - 1)"
        ></span>
      </div>

      <transition name="modal-fade">
        <div
          v-if="selectedCard"
          class="portal-modal-overlay"
          @click.self="closeInsightModal"
        >
          <div class="portal-modal">
            <button class="portal-modal-close" @click="closeInsightModal" aria-label="Close">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div class="portal-modal-header">
              <img
                class="portal-modal-logo"
                :src="newsIconMap[selectedCard.domain]"
                :alt="selectedCard.domain"
              />
              <div>
                <h4 class="portal-modal-title">
                  {{ selectedCard.domain }}
                </h4>
                <span class="portal-modal-subtitle">
                  {{ selectedCard.count }} articles were analyzed.
                </span>
              </div>
            </div>

            <div class="portal-modal-stats">
              <div class="portal-modal-stat">
                <span class="dot dot-positive"></span> Positive: {{ selectedCard.positive }}
              </div>
              <div class="portal-modal-stat">
                <span class="dot dot-neutral"></span> Neutral: {{ selectedCard.neutral }}
              </div>
              <div class="portal-modal-stat">
                <span class="dot dot-negative"></span> Negative: {{ selectedCard.negative }}
              </div>
            </div>

            <div class="portal-modal-body">
              <div class="portal-modal-block">
                <h5>Insight Summary</h5>
                <p>{{ selectedCard.insight?.insight_summary || '-' }}</p>
              </div>
              <div class="portal-modal-block">
                <h5>Media Focus</h5>
                <p>{{ selectedCard.insight?.media_focus || '-' }}</p>
              </div>
              <div class="portal-modal-block">
                <h5>PR Opportunity</h5>
                <p>{{ selectedCard.insight?.pr_opportunity || '-' }}</p>
              </div>
              <div class="portal-modal-block">
                <h5>Recommended Action</h5>
                <p>{{ selectedCard.insight?.recommended_action || '-' }}</p>
              </div>
              <div class="portal-modal-block portal-modal-block--risk">
                <h5>Risk Warning</h5>
                <p>{{ selectedCard.insight?.risk_warning || '-' }}</p>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <div>
      <Notification ref="alert"></Notification>
    </div>

    <SettingModal v-model="settingDialog" />
  </div>
</template>

<script>
import axios from "axios";
import { trackEvent } from '@/components/utils/utils.js';
import Notification from '@/components/Notification';
import SettingModal from '@/views/BrandAnalysis/components/SettingModal.vue';
export default {
  name: "NarrativeReputationClimate",
  components: {
    Notification: Notification,
    SettingModal: SettingModal,
  },
  data() {
    return {
      selectedGroupAnalysisKey: '',
      itemGroupAnalysis: [],
      isLoadingPublicRelationData: false,
      settingDialog: false,
      brandHealth: null,
      selectedCard: null,
      selectedBhsCard: null,
      industryPositive: 15,
      industryNeutral: 25,
      industryNegative: 60,
      brandPositive: 70,
      brandNeutral: 15,
      brandNegative: 15,
      prNewsInsights: [],
      currentPage: 0,
      cardsPerPage: 4,
      newsIconMap: {},
      selectedCard: null,
    };
  },
  mounted() {
    this.loadGroupAnalysis();
  },
  computed: {
    bhiScore() {
      const v = Number(this.brandHealth?.bhi_index);
      return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0;
    },
    bhiZone() {
      if (this.bhiScore < 33) return 'VULNERABLE';
      if (this.bhiScore < 66) return 'STABLE';
      return 'STRONG';
    },
    needle() {
      const rad = (this.bhiScore * 1.8 * Math.PI) / 180;
      const len = 79;
      return {
        x2: 110 - len * Math.cos(rad),
        y2: 110 - len * Math.sin(rad),
      };
    },
    statusArrow() {
      const status = (this.brandHealth?.insight_result?.overall_status || '').toUpperCase();

      if (!status) return '';

      if (/UPWARD|GROWTH|POSITIVE|INCREASE|RISING|STRONG/.test(status)) {
        return '↗';
      }

      if (/DOWNWARD|DECLINE|NEGATIVE|DECREASE|FALLING|WEAK/.test(status)) {
        return '↘';
      }

      return '→';
    },
    prNewsCards() {
      const circumference = 2 * Math.PI * 18;

      return this.prNewsInsights.map(item => {
        const total = item.positive + item.neutral + item.negative;
        const safeTotal = total > 0 ? total : 1;

        const positivePct = item.positive / safeTotal;
        const neutralPct = item.neutral / safeTotal;
        const negativePct = item.negative / safeTotal;

        const positiveLen = positivePct * circumference;
        const neutralLen = neutralPct * circumference;
        const negativeLen = negativePct * circumference;

        return {
          domain: item.domain,
          count: item.count,
          positive: item.positive,
          neutral: item.neutral,
          negative: item.negative,
          insight: item.insight || {},
          insightText: item.insight?.insight_summary || '',
          arcs: {
            positive: {
              dasharray: `${positiveLen} ${circumference - positiveLen}`,
              dashoffset: 0
            },
            neutral: {
              dasharray: `${neutralLen} ${circumference - neutralLen}`,
              dashoffset: -positiveLen
            },
            negative: {
              dasharray: `${negativeLen} ${circumference - negativeLen}`,
              dashoffset: -(positiveLen + neutralLen)
            }
          }
        };
      });
    },
    totalPages() {
      return Math.ceil(this.prNewsCards.length / this.cardsPerPage) || 1;
    },
    trackStyle() {
      return {
        transform: `translateX(-${this.currentPage * 100}%)`
      };
    }
  },
  methods: {
    async loadAllPublicRelationData() {
      if (!this.selectedGroupAnalysisKey) return;

      trackEvent('corporate-narrative-climate-apply-search', {
        title: `Corporate Narrative Climate Apply Search - ${this.selectedGroupAnalysisKey}`,
        groupAnalysis: this.selectedGroupAnalysisKey
      });

      this.brandHealth = null;
      this.prNewsInsights = [];

      this.isLoadingPublicRelationData = true;
      try {
        await Promise.all([
          this.loadBrandHealth(),
          this.loadPrNewsInsight(),
        ]);
      } finally {
        this.isLoadingPublicRelationData = false;
      }
    },
    async loadBrandHealth() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const payload = {
          group_analysis: this.selectedGroupAnalysisKey,
          start_date: "01-08-2026",
          end_date: "01-09-2026",
        };

        const response = await axiosInstance.post(
          '/gen-ai-medan/api/v1/chat/describe-brand-health',
          payload
        );

        this.brandHealth = response?.data?.data ?? null;
      } catch (err) {
        console.error('Error loadBrandHealth:', err);
        this.brandHealth = null;
      }
    },
    formatScore(v) {
      const n = Number(v);
      return Number.isFinite(n) ? Math.round(n) : '-';
    },
    openBhsModal(type) {
      const labels = {
        sentiment: {
          title: 'Weighted Sentiment',
          value: this.formatScore(this.brandHealth?.weighted_sentiment),
          text: this.brandHealth?.insight_result?.component_breakdown?.sentiment_analysis,
        },
        authority: {
          title: 'Authority Score',
          value: this.formatScore(this.brandHealth?.authority_tier),
          text: this.brandHealth?.insight_result?.component_breakdown?.authority_analysis,
        },
        sov: {
          title: 'Share of Voice',
          value: this.formatScore(this.brandHealth?.weighted_sov),
          text: this.brandHealth?.insight_result?.component_breakdown?.sov_analysis,
        },
      };
      this.selectedBhsCard = labels[type] || null;
    },
    closeBhsModal() {
      this.selectedBhsCard = null;
    },
    async loadPrNewsInsight() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const payload = {
          group_analysis: this.selectedGroupAnalysisKey,
          current_date: "2026-08-25"
        };

        const response = await axiosInstance.post('/gen-ai-medan/api/v1/chat/describe-pr-news', payload);

        this.prNewsInsights = response?.data?.data?.summary ?? [];
        if (this.prNewsInsights.length > 0) {
          this.fetchNewsIcons();
        }
      } catch (err) {
        console.error('Error loadPrNewsInsight:', err);
        this.prNewsInsights = [];
      }
    },
    async fetchNewsIcons() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const response = await axios.get(
          `/news-icon`,
          { headers: { 'auth-token': authToken } }
        );

        const data = response.data;
        const list = data && data.token ? data.token : [];

        const map = {};
        list.forEach((item) => {
          if (item && item.DOMAIN) {
            map[item.DOMAIN] = item.PRS_IMAGE;
          }
        });
        this.newsIconMap = map;
      } catch (e) {
        this.newsIconMap = {};

        if (e.response) {
          const { status } = e.response;

          switch (status) {
            case 404:
              this.$refs.alert.error_login(404);
              break;
            case 403:
              this.$refs.alert.error_login(403);
              break;
            default:
              console.log("some other error");
              break;
          }
        } else {
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 10000);
          this.message = "API News Icon Refused To Connect";
          this.colorAlert = "red";
        }
      }
    },
    prevSlide() {
      if (this.currentPage > 0) {
        this.currentPage -= 1;
      }
    },
    nextSlide() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage += 1;
      }
    },
    goToPage(n) {
      this.currentPage = Math.max(0, Math.min(n, this.totalPages - 1));
    },
    openInsightModal(card) {
      this.selectedCard = card;
    },
    closeInsightModal() {
      this.selectedCard = null;
    },
    async loadGroupAnalysis() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const response = await axios.get('/public_relation/group_analysis', {
          headers: {
            'auth-token': authToken,
          },
        });

        const list = response?.data?.token?.data ?? [];
        this.itemGroupAnalysis = list;

        if (list.length > 0) {
          this.selectedGroupAnalysisKey = list[0].GROUP_ANALYSIS;
          this.loadAllPublicRelationData();
        }
      } catch (e) {
        this.itemGroupAnalysis = [];

        if (e.response) {
          const { status } = e.response;

          switch (status) {
            case 404:
              this.$refs.alert.error_login(404);
              break;
            case 403:
              this.$refs.alert.error_login(403);
              break;
            default:
              console.log("some other error");
              break;
          }
        } else {
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 10000);
          this.message = "API Public Relation Group Analysis Refused To Connect";
          this.colorAlert = "red";
        }
      }
    }
  },
};
</script>

<style scoped>
.narrative-climate-wrapper {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding: 16px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.page-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.header-select-wrapper {
  position: relative;
  width: 195px;
  flex-shrink: 0;
}

.header-group-select {
  background-color: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #333;
  font-size: 14px;
  height: 47px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 40px 12px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.header-group-select:focus {
  border-color: #0471A5;
  box-shadow: 0 0 0 2px rgba(4, 113, 165, 0.15);
}

.header-chevron-icon {
  position: absolute;
  pointer-events: none;
  color: #666;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
}

.header-apply-btn {
  background-color: #000 !important;
  color: white !important;
  height: 47px !important;
  min-width: 0 !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 0 24px !important;
}

.header-apply-btn :deep(.v-btn__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  height: 100%;
}

.header-apply-btn :deep(.v-icon) {
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1;
  vertical-align: middle;
}

.reputation-gauge-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  gap: 16px;
}

.reputation-gauge-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 16px;
  flex: 0 0 60% !important;
  width: 60% !important;
  max-width: 60% !important;
  box-sizing: border-box !important;
}

.bhs-column {
  flex: 0 0 50% !important;
  width: 50% !important;
  max-width: 50% !important;
  box-sizing: border-box !important;
  display: flex;
  flex-direction: column;
  padding-left: 60px;
}

.bhs-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 14px;
}

.bhs-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 16px;
}

.bhs-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  width: 100%;
  max-width: 250px;
  min-height: 100px;
  padding: 10px 30px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #eef2f6;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.bhs-card--clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.bhs-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.14);
}

.bhs-icon-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex-shrink: 0;
  width: 140px;
  gap: 2px;
}

.bhs-svg-container {
  width: 60px;
  max-width: 60px;
  aspect-ratio: 1;
  margin-bottom: -6px;
}

.bhs-svg {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: scale(1.1);
}

.bhs-label {
  font-size: 15px;
  font-weight: 600;
  color: #111111;
  text-align: center;
  margin-top: -4px;
  z-index: 2;
}

.bhs-value {
  font-size: 28px;
  font-weight: 700;
  color: #111111;
  margin-left: auto;
}

.gauge-visual-column {
  flex: 0 0 50% !important;
  width: 50% !important;
  max-width: 50% !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
}

.gauge-title {
  text-align: center;
  transform: translateY(-6px);
  margin-bottom: 8px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 4px 0;
  letter-spacing: 0.3px;
}

.section-subtitle {
  font-size: 16px;
  color: #475569;
  margin: 0;
}

.gauge-render-container {
  width: 100%;
  transform: translateY(-8px);
  position: relative;
}

.gauge-svg {
  width: 100%;
  height: auto;
  display: block;
}

.gauge-center-text {
  font-size: 28px;
  font-weight: 800;
  fill: #0f172a;
}

.gauge-footer-title {
  font-size: 12px;
  font-weight: 750;
  color: #000000;
  letter-spacing: 0.2px;
}

.gauge-explanation-container {
  flex: 0 0 35% !important;
  width: 35% !important;
  max-width: 35% !important;
  box-sizing: border-box;
}

.explanation-paragraph {
  font-size: 14.5px;
  color: #000000;
  line-height: 1.6;
  margin: 10px 0 0 15px;
  text-align: justify;
}

.drop-cap-wrapper {
  float: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 15px;
  text-align: left;
}

.score-display {
  font-size: 52px;
  font-weight: 800;
  color: #000000;
  line-height: 1;
  margin-bottom: 5px;
  letter-spacing: -0.5px;
}

.trend-status {
  font-size: 15px;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  line-height: 1.2;
  margin-bottom: 2px;
  letter-spacing: 0.1px;
}

.trend-arrow, .inline-arrow {
  color: #24b46c;
  font-weight: 800;
  font-size: 18px;
  display: inline-block;
  margin: 0 2px;
}

.landscape-reputation-row {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 16px;
}

.landscape-card {
  flex: 1;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 18px 20px 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
}

.card-header-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  letter-spacing: 0.3px;
}

.card-subtitle {
  font-size: 16px;
  color: #475569;
  margin: 0;
}

.badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 2px 7px;
  border-radius: 3px;
  text-transform: uppercase;
  white-space: nowrap;
}

.hbar-wrapper {
  width: 100%;
}

.hbar {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 38px;
  border-radius: 4px;
  overflow: hidden;
}

.hbar-seg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
}

.hbar-label {
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.hbar-label--with-dot {
  display: flex;
  align-items: center;
  gap: 5px;
}

.hbar-seg--positive {
  background-color: #00E396;
}

.hbar-seg--neutral {
  background-color: #008FFB;
}

.hbar-seg--negative {
  background-color: #FF4560;
}

.dot-green {
  background-color: #22c55e;
  border: none;
}

.dot-blue {
  background-color: #0070C8;
  border: none;
}

.dot-red {
  background-color: #C62838;
  border: none;
}

.card-bottom {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 2px;
}

.card-bottom-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-bottom-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.key-drivers-label {
  font-size: 12px;
  color: #000;
  margin: 0;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.key-drivers-text {
  font-size: 13px;
  color: #000;
  margin: 0;
  line-height: 1.5;
}

.weather-label {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  margin: 0;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
}

.weather-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-portal-section {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 18px 16px 18px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  margin-top: 22px;
  margin-bottom: 16px;
}

.media-portal-title {
  font-size: 17px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 14px 0;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.carousel-outer {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.carousel-arrow {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  align-self: center;
  border-radius: 50%;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.15s;
}

.carousel-arrow:hover {
  background-color: #f1f5f9;
}

.carousel-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

.carousel-track-wrapper {
  flex: 1;
  overflow: hidden;
}

.carousel-track {
  display: flex;
  flex-direction: row;
  gap: 12px;
  will-change: transform;
}

.portal-card {
  flex: 0 0 calc(25% - 9px);
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 13px 13px 13px 13px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin-bottom: 5px;
}

.portal-card-top {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 52px;
}

.portal-logo-img {
  max-width: 110px;
  max-height: 32px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin-top: 6px;
}

.portal-donut {
  flex-shrink: 0;
}

.portal-card-body {
  flex: 1;
}

.portal-card-text {
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1.55;
  margin: 0;
}

.portal-card-text strong {
  font-weight: 700;
  color: #000000;
}

.carousel-dots {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #cbd5e1;
  cursor: pointer;
  border: none;
  padding: 0;
  display: inline-block;
  transition: background-color 0.15s, transform 0.15s;
}

.carousel-dot--active {
  background-color: #000;
  transform: scale(1.25);
}

.portal-card {
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}

.portal-card:hover {
  box-shadow: 0 4px 10px rgba(0,0,0,0.07);
  transform: translateY(-2px);
}

.portal-modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.portal-modal {
  background-color: #ffffff;
  border-radius: 10px;
  padding: 24px;
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  position: relative;
}

.portal-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.portal-modal-close:hover {
  background-color: #f1f5f9;
}

.portal-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding-right: 24px;
}

.portal-modal-logo {
  max-width: 90px;
  max-height: 30px;
  object-fit: contain;
}

.portal-modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  text-transform: uppercase;
}

.portal-modal-subtitle {
  font-size: 13px;
  color: #334155;
}

.portal-modal-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.portal-modal-stat {
  font-size: 12.5px;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.dot-positive {
  background-color: #00E396;
}

.dot-neutral {
  background-color: #008FFB;
}

.dot-negative {
  background-color: #FF4560;
}

.portal-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.portal-modal-block h5 {
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.portal-modal-block p {
  font-size: 13.5px;
  line-height: 1.55;
  color: #334155;
  margin: 0;
}

.portal-modal-block--risk {
  background-color: #fef2f2;
  border-left: 3px solid #FF4560;
  padding: 10px 12px;
  border-radius: 4px;
}

.portal-modal-block--risk h5 {
  color: #b91c1c;
}

.header-setting-btn {
  background-color: #0471A5 !important;
  color: white !important;
  height: 45px !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 12px 24px !important;
}
</style>